import { useMemo, useState, type FormEvent } from 'react';
import { Reply, Send } from 'lucide-react';
import { toast } from 'sonner';
import { postSupportTicketComments } from '@/api/control/control-plane/control-plane';
import type { SupportTicketDetailDto } from '@/api/control/missionImpactAPI.schemas';
import { AttachmentPicker } from '@/components/support/AttachmentPicker';
import { SupportMessage } from '@/components/support/SupportMessage';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useError } from '@/contexts/ErrorContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSupportAttachments } from '@/hooks/useSupportAttachments';
import {
  fillSupportTemplate,
  getCommentAttachments,
  getSupportTicketState,
  splitConversation,
} from '@/lib/support';

const COMMENT_MAX_LENGTH = 10_000;

interface SupportConversationProps {
  orgId: string;
  ticket: SupportTicketDetailDto;
  /** Called after a reply is posted so the ticket can be refetched. */
  onPosted: () => Promise<unknown>;
}

/**
 * A ticket's conversation. The reply box sits at the top, next to the latest context, with a
 * "Your turn" prompt when support is waiting on the customer. The AI summary is pinned first, then
 * the remaining replies newest first. Files attached to a reply are uploaded first and posted with it.
 */
export function SupportConversation({ orgId, ticket, onPosted }: SupportConversationProps) {
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const ticketId = ticket.id ?? '';
  const { policy, validate, upload, hint } = useSupportAttachments(orgId);

  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isPosting, setIsPosting] = useState(false);

  const { aiSummary, replies } = useMemo(() => splitConversation(ticket.comments), [ticket.comments]);
  const cleanAttachmentIds = useMemo(
    () =>
      new Set(
        (ticket.attachments ?? []).flatMap((attachment) =>
          attachment.scanStatus === 'Clean' && attachment.id ? [attachment.id] : []
        )
      ),
    [ticket.attachments]
  );
  const isYourTurn = getSupportTicketState(ticket) === 'WaitingOnYou';

  /**
   * Adds picked files that pass the attachment policy, reporting the rest.
   */
  const handlePick = (picked: File[]) => {
    const { accepted, errors: rejected } = validate(picked);
    setFiles((current) => [...current, ...accepted]);
    setErrors(rejected);
  };

  /**
   * Uploads the reply's files, then posts the reply with the uploaded attachments linked to it.
   * Files that fail to upload are reported and kept in the picker for the next reply.
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const text = comment.trim();
    if (!text) {
      setErrors([t.support.detail.commentRequired]);
      return;
    }
    if (text.length > COMMENT_MAX_LENGTH) {
      setErrors([fillSupportTemplate(t.support.detail.commentTooLong, { max: COMMENT_MAX_LENGTH })]);
      return;
    }

    setErrors([]);
    setIsPosting(true);
    try {
      const { uploaded, failed } = files.length > 0 ? await upload(ticketId, files) : { uploaded: [], failed: [] };
      const attachmentIds = uploaded.flatMap((attachment) => (attachment.id ? [attachment.id] : []));
      await postSupportTicketComments(orgId, ticketId, {
        comment: text,
        attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
      setComment('');
      // Keep files that failed to upload so they can go with the next reply.
      setFiles(failed);
      setErrors(failed.map((f) => fillSupportTemplate(t.support.detail.uploadFailed, { name: f.name })));
      await onPosted();
    } catch (error) {
      handleApiError(error, { context: 'SupportConversation.handleSubmit' }, { showToast: false });
      toast.error(t.support.detail.commentFailed);
    } finally {
      setIsPosting(false);
    }
  };

  const replyCount = replies.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t.support.detail.conversationTitle}</CardTitle>
        {replyCount > 0 && (
          <span className="text-xs text-muted-foreground">
            {fillSupportTemplate(t.support.detail.replyCount, { count: replyCount })}
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <form className="space-y-3 border-b pb-6" noValidate onSubmit={(e) => { void handleSubmit(e); }}>
          {isYourTurn && (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium background-warning-strong text-warning-strong">
                <Reply className="h-3 w-3" aria-hidden />
                {t.support.detail.yourTurn}
              </span>
              {t.support.detail.yourTurnBody}
            </div>
          )}
          <Label htmlFor="support-comment">{t.support.detail.commentLabel}</Label>
          <Textarea
            id="support-comment"
            rows={4}
            value={comment}
            placeholder={t.support.detail.commentPlaceholder}
            aria-invalid={errors.length > 0 && !comment.trim()}
            onChange={(e) => { setComment(e.target.value); }}
          />
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                {errors.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <AttachmentPicker
                files={files}
                accept={policy?.allowedExtensions}
                buttonLabel={t.support.detail.attachToReply}
                disabled={isPosting}
                onPick={handlePick}
                onRemove={(file) => { setFiles((current) => current.filter((f) => f !== file)); }}
              />
              {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            </div>
            <Button type="submit" disabled={isPosting}>
              <Send className="mr-2 h-4 w-4" />
              {isPosting ? t.support.detail.posting : t.support.detail.postComment}
            </Button>
          </div>
        </form>

        {!aiSummary && replies.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.support.detail.noComments}</p>
        ) : (
          <ol className="space-y-4">
            {aiSummary && (
              <SupportMessage
                orgId={orgId}
                ticketId={ticketId}
                comment={aiSummary}
                attachments={getCommentAttachments(aiSummary, ticket.attachments)}
                cleanAttachmentIds={cleanAttachmentIds}
                isAiSummary
              />
            )}
            {replies.map((reply) => (
              <SupportMessage
                key={reply.id}
                orgId={orgId}
                ticketId={ticketId}
                comment={reply}
                attachments={getCommentAttachments(reply, ticket.attachments)}
                cleanAttachmentIds={cleanAttachmentIds}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
